import { supabase } from '../lib/supabase';
import { userProfileService } from './userProfileService';

const toSnakeCase = (tripData) => ({
  trip_name: tripData?.tripName,
  crew_size: tripData?.crewSize,
  duration: tripData?.duration,
  start_date: tripData?.startDate,
  end_date: tripData?.endDate,
  status: tripData?.status,
  has_fridge: tripData?.hasFridge,
  has_freezer: tripData?.hasFreezer,
  has_ice_cooler: tripData?.hasIceCooler,
  has_stove: tripData?.hasStove,
  has_oven: tripData?.hasOven,
  has_grill: tripData?.hasGrill,
  has_microwave: tripData?.hasMicrowave,
  has_air_fryer: tripData?.hasAirFryer,
  dietary_restrictions: tripData?.dietaryRestrictions,
  is_premium: tripData?.isPremium,
  completed_days: tripData?.completedDays
});

const toCamelCase = (row) => ({
  id: row?.id,
  userId: row?.user_id,
  tripName: row?.trip_name,
  crewSize: row?.crew_size,
  duration: row?.duration,
  startDate: row?.start_date,
  endDate: row?.end_date,
  status: row?.status,
  hasFridge: row?.has_fridge,
  hasFreezer: row?.has_freezer,
  hasIceCooler: row?.has_ice_cooler,
  hasStove: row?.has_stove,
  hasOven: row?.has_oven,
  hasGrill: row?.has_grill,
  hasMicrowave: row?.has_microwave,
  hasAirFryer: row?.has_air_fryer,
  dietaryRestrictions: row?.dietary_restrictions,
  isPremium: row?.is_premium,
  completedDays: row?.completed_days || [],
  createdAt: row?.created_at,
  updatedAt: row?.updated_at
});

export const tripService = {
  async getActiveTrip(userId) {
    try {
      const { data, error } = await supabase?.from('trips')?.select('*')?.eq('user_id', userId)?.eq('status', 'active')?.single();
      
      if (error) {
        if (error?.code === 'PGRST116') {
          return { data: null, error: null };
        }
        throw error;
      }
      
      return { data: data ? toCamelCase(data) : null, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getAllTrips(userId) {
    try {
      const { data, error } = await supabase?.from('trips')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return { data: data?.map(toCamelCase) || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async createTrip(tripData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('trips')?.insert({
          user_id: user?.id,
          ...toSnakeCase(tripData)
        })?.select()?.single();
      
      if (error) throw error;
      
      // Set trial_start_date when user creates their first trip
      if (!tripData?.isPremium) {
        await userProfileService?.updateProfile(user?.id, {
          trialStartDate: new Date()?.toISOString()
        });
      }
      
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateTrip(tripId, tripData) {
    try {
      const { data, error } = await supabase?.from('trips')?.update(toSnakeCase(tripData))?.eq('id', tripId)?.select()?.single();
      
      if (error) throw error;
      
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteTrip(tripId) {
    try {
      const { error } = await supabase?.from('trips')?.delete()?.eq('id', tripId);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async updateCompletedDays(tripId, completedDays) {
    try {
      const { data, error } = await supabase
        ?.from('trips')
        ?.update({ completed_days: completedDays })
        ?.eq('id', tripId)
        ?.select()
        ?.single();
      
      if (error) throw error;
      
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};